<template>
  <footer class="footer">
    <!-- Top Gradient Line -->
    <div class="footer__top-line"></div>
    
    <div class="container">
      <!-- Main Footer Grid -->
      <div class="footer__grid">
        <!-- Brand Column -->
        <div class="footer__brand">
          <div class="footer__logo">
            <span class="footer__logo-text">{{ cms.general.site_name || 'Inversun' }}</span>
          </div>
          <p class="footer__tagline">
            {{ cms.footer.tagline || 'Premium solar energy solutions for a sustainable future.' }}
          </p>
          <div class="footer__social">
            <a v-if="cms.social.show_facebook" :href="cms.social.facebook" target="_blank" rel="noopener" class="footer__social-link" aria-label="Facebook">
              <Facebook :size="20" />
            </a>
            <a v-if="cms.social.show_instagram" :href="cms.social.instagram" target="_blank" rel="noopener" class="footer__social-link" aria-label="Instagram">
              <Instagram :size="20" />
            </a>
            <a v-if="cms.social.show_tiktok" :href="cms.social.tiktok" target="_blank" rel="noopener" class="footer__social-link" aria-label="TikTok">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
            <a v-if="cms.social.show_linkedin" :href="cms.social.linkedin" target="_blank" rel="noopener" class="footer__social-link" aria-label="LinkedIn">
              <Linkedin :size="20" />
            </a>
          </div>
        </div>

        <!-- Products Column -->
        <div class="footer__column">
          <h4 class="footer__heading">Products</h4>
          <nav class="footer__nav">
            <router-link to="/products/category/inverters" class="footer__link">Solar Pump Inverters</router-link>
            <router-link to="/products/category/reactors-3" class="footer__link">Reactors 3%</router-link>
            <router-link to="/products/category/reactors-5" class="footer__link">Reactors 5%</router-link>
            <router-link to="/products/category/sinewave-filters" class="footer__link">Sinewave Filters</router-link>
            <router-link to="/products/category/iot-modules" class="footer__link">WiFi & 4G Modules</router-link>
          </nav>
        </div>

        <!-- Company Column -->
        <div class="footer__column">
          <h4 class="footer__heading">Company</h4>
          <nav class="footer__nav">
            <router-link to="/about" class="footer__link">About Us</router-link>
            <router-link to="/services" class="footer__link">Service Center</router-link>
            <router-link to="/distributors" class="footer__link">Distributors</router-link>
            <router-link to="/support" class="footer__link">Support</router-link>
            <router-link to="/contact" class="footer__link">Contact</router-link>
          </nav>
        </div>

        <!-- Contact Column -->
        <div class="footer__column">
          <h4 class="footer__heading">Contact</h4>
          <div class="footer__contact">
            <div class="footer__contact-item">
              <MapPin :size="16" />
              <span>{{ cms.contact.address || 'Zahle-Baalbeck Highway, Bekaa, Lebanon' }}</span>
            </div>
            <a :href="`tel:${cms.contact.phone}`" class="footer__contact-item footer__contact-item--link">
              <Phone :size="16" />
              <span>{{ cms.contact.phone || '+961 76 984 611' }}</span>
            </a>
            <a :href="`mailto:${cms.contact.email}`" class="footer__contact-item footer__contact-item--link">
              <Mail :size="16" />
              <span>{{ cms.contact.email || 'sales@inversun.tech' }}</span>
            </a>
            <div class="footer__contact-item">
              <Clock :size="16" />
              <span>{{ cms.contact.business_hours || 'Mon-Sat: 9AM - 6PM' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="footer__bottom">
        <p class="footer__copyright">
          © {{ new Date().getFullYear() }} {{ cms.general.site_name || 'Inversun' }}. All rights reserved.
        </p>
        <div class="footer__legal">
          <router-link v-if="cms.footer.show_privacy_policy" to="/privacy">Privacy Policy</router-link>
          <router-link v-if="cms.footer.show_terms" to="/terms">Terms of Service</router-link>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { Facebook, Instagram, Linkedin, MapPin, Phone, Mail, Clock } from 'lucide-vue-next'
import { useCms } from '@/composables/useCms'

const cms = useCms()
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$primary-700: #1D4ED8;
$gold: #F59E0B;
$accent-cyan: #06B6D4;
$dark-900: #0f172a;
$dark-800: #1e293b;
$dark-700: #334155;

.footer {
  background: $dark-900;
  color: #94a3b8;

  &__top-line {
    height: 3px;
    background: linear-gradient(90deg, $primary 0%, $gold 50%, $primary 100%);
    background-size: 200% 100%;
    animation: gradient-flow 3s linear infinite;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1.5fr 1fr 1fr 1fr;
    gap: 3rem;
    padding: 3rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
      gap: 2rem;
      padding: 2rem 0;
    }
  }

  &__brand {
    @media (max-width: 1024px) {
      grid-column: 1 / -1;
    }

    @media (max-width: 640px) {
      grid-column: auto;
    }
  }

  &__logo {
    margin-bottom: 1rem;
  }

  &__logo-text {
    font-family: 'Outfit', sans-serif;
    font-size: 1.75rem;
    font-weight: 800;
    background: linear-gradient(135deg, $primary 0%, $gold 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &__tagline {
    font-size: 0.95rem;
    line-height: 1.7;
    margin-bottom: 1.5rem;
    max-width: 280px;
  }

  &__social {
    display: flex;
    gap: 0.75rem;
  }

  &__social-link {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    color: #94a3b8;
    transition: all 0.25s ease;

    &:hover {
      background: $primary;
      border-color: $primary;
      color: white;
      transform: translateY(-2px);
    }
  }

  &__column {
    @media (max-width: 640px) {
      padding-top: 1rem;
      border-top: 1px solid rgba(255, 255, 255, 0.06);
    }
  }

  &__heading {
    font-family: 'Outfit', sans-serif;
    font-size: 1rem;
    font-weight: 600;
    color: white;
    margin-bottom: 1.25rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__nav {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__link {
    color: #94a3b8;
    text-decoration: none;
    font-size: 0.9rem;
    transition: all 0.2s ease;
    display: inline-block;

    &:hover {
      color: $gold;
      transform: translateX(4px);
    }
  }

  &__contact {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
  }

  &__contact-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    font-size: 0.9rem;
    line-height: 1.5;
    color: #94a3b8;
    text-decoration: none;
    transition: color 0.2s ease;

    svg {
      color: $gold;
      flex-shrink: 0;
      margin-top: 2px;
    }

    &--link:hover {
      color: $gold;
    }

    span {
      word-break: break-word;
    }
  }

  &__bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
    padding-top: 1.5rem;

    @media (max-width: 640px) {
      flex-direction: column;
      text-align: center;
    }
  }

  &__copyright {
    font-size: 0.85rem;
    color: #64748b;
  }

  &__legal {
    display: flex;
    gap: 1.5rem;

    a {
      font-size: 0.85rem;
      color: #64748b;
      text-decoration: none;
      transition: color 0.2s ease;

      &:hover {
        color: $gold;
      }
    }

    @media (max-width: 640px) {
      gap: 1rem;
    }
  }
}

@keyframes gradient-flow {
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
}
</style>
